import { apiFetch } from './api';
import {
  invalidateCachedResource,
  invalidateCachedResourcesByPrefix,
  readCachedResource,
} from './requestCache';

export type ServiceWorkspaceOrganization = {
  id: string;
  name: string;
  businessName: string | null;
  logoUrl: string | null;
  organizationType: string;
  primaryAccountHolder: {
    id: string;
    displayName: string | null;
    email: string | null;
    photoUrl: string | null;
  } | null;
};

export type ServiceWorkspaceOrganizationOption = {
  id: string;
  name: string;
  businessName: string | null;
  logoUrl: string | null;
  organizationType: string;
};

export type ServiceWorkspaceViewer = {
  role: string;
  userId: string | null;
  canManageServices: boolean;
  isPrimaryAccountHolder: boolean;
};

export type ServiceWorkspaceCurrentService = {
  id: string;
  status: string;
  source: string;
  sourceRef: string | null;
  attachedOrganization: {
    id: string;
    name: string | null;
    businessName: string | null;
    logoUrl: string | null;
    organizationType: string | null;
  } | null;
  serviceCode: string | null;
  serviceName: string | null;
  tierSkuId: string | null;
  tierName: string | null;
  contractTerm: string | null;
  contractedMonthlyMinutesMin: number | null;
  contractedMonthlyMinutesMax: number | null;
  selectedOfferings: string[];
  selectedOfferingCodes: string[];
  addOnNames: string[];
  startsAt: string | null;
  endsAt: string | null;
  billingSubscriptionId: string | null;
  orderId: string | null;
  displaySnapshot?: {
    offerings?: Array<{
      code?: string | null;
      name?: string | null;
      type?: string | null;
      source?: string | null;
    }>;
    [key: string]: unknown;
  } | null;
};

export type ServiceWorkspaceProposal = {
  id: string;
  orgId: string;
  billingOwnerUserId: string | null;
  title: string;
  summary: string | null;
  status: string;
  proposalType: string;
  reviewerNote: string | null;
  serviceCategory: string | null;
  serviceCode: string | null;
  serviceName: string | null;
  tierSkuId: string | null;
  tierName: string | null;
  recurringMonthlyCents: number;
  totalDueNowCents: number;
  subtotalOneTimeCents: number;
  discountCents: number;
  dueNowDynamic?: boolean;
  requestedAction?: string | null;
  selectedOfferings: string[];
  addOnNames: string[];
  lineItems: Array<Record<string, unknown>>;
  cartItems: Array<Record<string, unknown>>;
  laneSelections: Array<Record<string, unknown>>;
  sentAt: string | null;
  viewedAt: string | null;
  checkoutStartedAt: string | null;
  rejectedAt: string | null;
  appliedAt: string | null;
  canceledAt: string | null;
  expiresAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  relatedOrderId: string | null;
  configurationSnapshot: Record<string, unknown>;
};

export type ServiceWorkspaceSummary = {
  organization: ServiceWorkspaceOrganization;
  viewer: ServiceWorkspaceViewer;
  manageableOrganizations: ServiceWorkspaceOrganizationOption[];
  currentServices: ServiceWorkspaceCurrentService[];
  transferableContracts: ServiceWorkspaceCurrentService[];
  pendingProposals: ServiceWorkspaceProposal[];
  proposalHistory: ServiceWorkspaceProposal[];
};

export type ServiceCatalogOffering = {
  id: string | null;
  code: string | null;
  name: string | null;
  description: string | null;
  sortOrder: number;
  status: string | null;
  metadata: Record<string, unknown>;
};

export type ServiceCatalogTier = {
  id: string | null;
  skuId: string | null;
  name: string | null;
  description: string | null;
  billingType: string | null;
  billingInterval: string | null;
  amountCents: number;
  currency: string;
  isPrimaryService: boolean;
  requiredOnboardingSkuId: string | null;
  metadata: Record<string, unknown>;
  offerings: ServiceCatalogOffering[];
};

export type ServiceCatalogAddOn = {
  skuId: string | null;
  name: string | null;
  description: string | null;
  billingType: string | null;
  amountCents: number;
  currency: string;
  metadata: Record<string, unknown>;
};

export type ServiceCatalogService = {
  code: string | null;
  slug: string | null;
  category: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  teaser: string | null;
  checkoutEnabled: boolean;
  metadata: Record<string, unknown>;
  addOns: ServiceCatalogAddOn[];
  tiers: ServiceCatalogTier[];
};

export type ServiceWorkspaceCatalog = {
  services: ServiceCatalogService[];
};

export type ServiceCheckoutSessionStatus = {
  status: 'paid' | 'pending' | 'expired' | 'failed' | 'unknown';
  orderId: string | null;
  customerEmail: string | null;
  accountAction: 'create_account' | 'proceed_dashboard' | 'unknown';
  code:
    | 'SESSION_NOT_FOUND'
    | 'AWAITING_STRIPE_CONFIRMATION'
    | 'ACCOUNT_SETUP_REQUIRED'
    | 'PAYMENT_CONFIRMED'
    | 'CHECKOUT_EXPIRED'
    | 'CHECKOUT_FAILED'
    | 'STATUS_UNMAPPED';
  message: string;
};

export type ServiceCheckoutPayload = {
  orgId?: string;
  cartItems: Array<{ skuId: string; quantity: number }>;
  laneSelections?: Array<{
    tierSkuId: string;
    laneIds: string[];
    laneLabels?: string[];
  }>;
  successUrl: string;
  cancelUrl: string;
  returnUrl?: string;
  uiMode?: 'hosted' | 'embedded' | 'custom';
  discountCode?: string;
};

export type CurrentServiceMutationPayload = {
  orgId: string;
  currentServiceId: string;
  tierSkuId: string;
  laneSelections?: Array<{
    tierSkuId: string;
    laneIds: string[];
    laneLabels?: string[];
  }>;
  discountCode?: string;
  paymentMethodId?: string;
  prorationDate?: number;
  paymentIntentId?: string;
};

export type CurrentServiceMutationResponse = {
  action: 'no_change' | 'update' | 'upgrade';
  status: 'no_change' | 'updated' | 'requires_action' | 'upgraded';
  clientSecret?: string | null;
  piStatus?: string | null;
  paymentIntentId?: string | null;
  currentService?: ServiceWorkspaceCurrentService;
};

export type CurrentServicePreviewResponse = {
  action: 'no_change' | 'update' | 'upgrade';
  status: 'no_change' | 'updated' | 'preview';
  dueNowCents: number;
  recurringMonthlyCents: number;
  discountCents: number;
  currency: string;
  prorationDate: number | null;
};

export type ServiceProposalPayload = {
  orgId: string;
  title: string;
  summary?: string;
  proposalType?: string;
  serviceCategory?: string;
  serviceCode?: string;
  serviceName?: string;
  tierSkuId?: string;
  tierName?: string;
  cartItems: Array<{ skuId: string; quantity: number }>;
  laneSelections?: Array<{
    tierSkuId: string;
    laneIds: string[];
    laneLabels?: string[];
  }>;
  configurationSnapshot?: Record<string, unknown>;
  expiresAt?: string;
};

type ClientServiceFetchOptions = {
  force?: boolean;
};

const CLIENT_SERVICE_CACHE_TTL_MS = 60_000;
const SERVICE_SUMMARY_CACHE_PREFIX = 'client-services:summary:';
const SERVICE_CATALOG_CACHE_KEY = 'client-services:catalog';

function buildSummaryCacheKey(orgId?: string): string {
  const scope = orgId?.trim() || 'self';
  return `${SERVICE_SUMMARY_CACHE_PREFIX}${scope}`;
}

export function invalidateServiceWorkspaceSummaryCache(orgId?: string): void {
  if (orgId?.trim()) {
    invalidateCachedResource(buildSummaryCacheKey(orgId));
  }
  invalidateCachedResource(buildSummaryCacheKey());
  invalidateCachedResourcesByPrefix(SERVICE_SUMMARY_CACHE_PREFIX);
}

export function invalidateServiceWorkspaceCatalogCache(): void {
  invalidateCachedResource(SERVICE_CATALOG_CACHE_KEY);
}

export async function fetchServiceWorkspaceSummary(
  orgId?: string,
  options: ClientServiceFetchOptions = {},
) {
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
  return readCachedResource(
    buildSummaryCacheKey(orgId),
    () => apiFetch<ServiceWorkspaceSummary>(`/api/client-services/summary${query}`),
    {
      force: options.force,
      ttlMs: CLIENT_SERVICE_CACHE_TTL_MS,
    },
  );
}

export async function fetchServiceWorkspaceCatalog(
  options: ClientServiceFetchOptions = {},
) {
  return readCachedResource(
    SERVICE_CATALOG_CACHE_KEY,
    () => apiFetch<ServiceWorkspaceCatalog>('/api/client-services/catalog'),
    {
      force: options.force,
      ttlMs: CLIENT_SERVICE_CACHE_TTL_MS,
    },
  );
}

export async function createClientServiceCheckoutSession(
  payload: ServiceCheckoutPayload,
) {
  return apiFetch<{
    orderId: string;
    checkoutSessionId: string;
    checkoutUrl: string | null;
    clientSecret: string | null;
    uiMode: 'hosted' | 'embedded' | 'custom';
  }>('/api/client-services/checkout-session', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function changeCurrentClientService(
  payload: CurrentServiceMutationPayload,
) {
  const response = await apiFetch<CurrentServiceMutationResponse>(
    '/api/client-services/current-service/change',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  invalidateServiceWorkspaceSummaryCache(payload.orgId);
  return response;
}

export async function previewCurrentClientServiceChange(
  payload: CurrentServiceMutationPayload,
) {
  return apiFetch<CurrentServicePreviewResponse>(
    '/api/client-services/current-service/preview',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function finalizeCurrentClientServiceChange(
  payload: CurrentServiceMutationPayload,
) {
  const response = await apiFetch<CurrentServiceMutationResponse>(
    '/api/client-services/current-service/finalize',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  invalidateServiceWorkspaceSummaryCache(payload.orgId);
  return response;
}

export async function transferCurrentClientServiceToOrganization(payload: {
  orgId: string;
  currentServiceId: string;
}) {
  const response = await apiFetch<{
    transferred: boolean;
    currentService: ServiceWorkspaceCurrentService;
  }>('/api/client-services/current-service/transfer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  invalidateServiceWorkspaceSummaryCache(payload.orgId);
  return response;
}

export async function createClientServiceProposal(
  payload: ServiceProposalPayload,
) {
  const response = await apiFetch<{ proposal: ServiceWorkspaceProposal }>(
    '/api/client-services/proposals',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  invalidateServiceWorkspaceSummaryCache(payload.orgId);
  return response;
}

export async function markClientServiceProposalViewed(proposalId: string) {
  const response = await apiFetch<{ proposal: ServiceWorkspaceProposal }>(
    `/api/client-services/proposals/${encodeURIComponent(proposalId)}/view`,
    {
      method: 'POST',
    },
  );
  invalidateServiceWorkspaceSummaryCache();
  return response;
}

export async function rejectClientServiceProposal(
  proposalId: string,
  reviewerNote?: string,
) {
  const response = await apiFetch<{ proposal: ServiceWorkspaceProposal }>(
    `/api/client-services/proposals/${encodeURIComponent(proposalId)}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({ reviewerNote }),
    },
  );
  invalidateServiceWorkspaceSummaryCache();
  return response;
}

export async function cancelClientServiceProposal(
  proposalId: string,
  reviewerNote?: string,
) {
  const response = await apiFetch<{ proposal: ServiceWorkspaceProposal }>(
    `/api/client-services/proposals/${encodeURIComponent(proposalId)}/cancel`,
    {
      method: 'POST',
      body: JSON.stringify({ reviewerNote }),
    },
  );
  invalidateServiceWorkspaceSummaryCache();
  return response;
}

export async function createClientServiceProposalCheckoutSession(
  proposalId: string,
  payload: Omit<ServiceCheckoutPayload, 'orgId' | 'cartItems' | 'laneSelections'>,
) {
  return apiFetch<{
    proposal: ServiceWorkspaceProposal;
    checkoutSession: {
      orderId: string;
      checkoutSessionId: string;
      checkoutUrl: string | null;
      clientSecret: string | null;
      uiMode: 'hosted' | 'embedded' | 'custom';
    };
  }>(
    `/api/client-services/proposals/${encodeURIComponent(proposalId)}/checkout-session`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchServiceCheckoutSessionStatus(sessionId: string) {
  return apiFetch<ServiceCheckoutSessionStatus>(
    `/api/funnel/checkout/session/${encodeURIComponent(sessionId)}/status`,
  );
}
